import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  ArrowLeft, ChevronLeft, ChevronRight, Pause, Play, RefreshCw, Send, X, Zap,
} from "lucide-react";
import "./desk.css";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";
const API     = `${API_URL}/api`;

// ── Formatting ────────────────────────────────────────────────────────────────
// The backend sends every time as UTC with an offset plus the operator's zone
// (TEAM_TIMEZONE). Everything shown here is in that zone and labelled.

function tzLabel(date, timeZone) {
  try {
    const part = new Intl.DateTimeFormat("en-US", { timeZone, timeZoneName: "short" })
      .formatToParts(date).find(p => p.type === "timeZoneName")?.value || "";
    // CDT / CST → CT, matching the shell's labels.
    return /^[ECMPAH][SD]T$/.test(part) ? `${part[0]}T` : part;
  } catch { return "UTC"; }
}

function fmtTime(iso, timeZone, { day = false } = {}) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  try {
    const now = new Date();
    const sameDay = new Intl.DateTimeFormat("en-US", { timeZone, dateStyle: "short" }).format(d)
      === new Intl.DateTimeFormat("en-US", { timeZone, dateStyle: "short" }).format(now);
    const t = new Intl.DateTimeFormat("en-US", {
      timeZone, hour: "numeric", minute: "2-digit",
      ...(day || !sameDay ? { weekday: "short" } : {}),
    }).format(d);
    return `${t} ${tzLabel(d, timeZone)}`;
  } catch {
    return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  }
}

function fmtAge(iso) {
  if (!iso) return "";
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const h = Math.round(mins / 60);
  if (h < 36) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

const NUM = ["No", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve"];
const numWord = (n, lower = false) => {
  const w = NUM[n] ?? String(n);
  return lower ? w.toLowerCase() : w;
};

// "an 80% floor", "a 75% floor"
const article = (n) => (/^(8|11|18)/.test(String(n)) ? "an" : "a");

function wantsLabel(req) {
  if (!req) return "";
  const p = req.params || {};
  if (req.type === "raise_confidence") return `Wants ${article(req.value)} ${req.value}% floor`;
  if (req.type === "block_direction") return `Wants new ${p.direction || ""}s blocked`;
  if (req.type === "avoid_symbol") return `Wants to skip ${p.symbol}`;
  return "Wants a decision";
}

const TYPE_ASK = {
  raise_confidence: "a higher confidence floor",
  block_direction: "to block a trade direction",
  avoid_symbol: "to skip a symbol",
};
const TYPE_REASSURE = {
  raise_confidence: "New trades below the floor are blocked until it expires. Open positions are never touched.",
  block_direction: "New trades in that direction are blocked until it expires. Open positions are never touched.",
  avoid_symbol: "New trades on that symbol are blocked until it expires. Open positions are never touched.",
};

const STANCE_ORDER = { bearish: 0, bullish: 0, neutral: 1, none: 2 };
const isLeaning = (a) => a.stance === "bearish" || a.stance === "bullish";

const STARTERS = [
  "What would change your mind?",
  "Walk me through the numbers behind your take.",
  "What should I watch before the next cycle?",
];
const SUGGESTED = [
  "Where do you disagree with the desk?",
  "What would change your stance today?",
];

async function getJSON(path, opts) {
  const res = await fetch(`${API}${path}`, opts);
  return res.json();
}
const postJSON = (path, body) => getJSON(path, {
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: body === undefined ? undefined : JSON.stringify(body),
});

// ── Small pieces ──────────────────────────────────────────────────────────────

function Avatar({ agent, size = "md" }) {
  return (
    <span className={`dk-avatar dk-avatar-${size}`} style={{ "--agent": agent.color }} aria-hidden>
      {agent.emoji}
    </span>
  );
}

function StancePill({ agent }) {
  const word = agent.stance === "none" ? agent.stance_word || "No report" : agent.stance_word;
  const conv = agent.conviction != null ? ` ${agent.conviction}%` : "";
  return <span className={`dk-pill is-${agent.stance}`}>{word}{agent.stance === "none" ? "" : conv}</span>;
}

// ── Page bar ──────────────────────────────────────────────────────────────────

function PageBar({ desk, onRefresh, onRun, running, refreshing }) {
  const tz = desk?.timezone;
  const agents = desk?.agents || [];
  const cyc = desk?.cycle || {};
  const parts = [`${agents.length || "—"} agents`];
  if (cyc.running || running) parts.push("a cycle is running now");
  else if (cyc.at) parts.push(`${cyc.reported} reported at ${fmtTime(cyc.at, tz)}`);
  if (!(cyc.running || running) && desk?.next_cycle_at) parts.push(`next cycle ${fmtTime(desk.next_cycle_at, tz)}`);
  const inForce = desk?.active?.length || 0;
  if (inForce) parts.push(`${inForce} guardrail${inForce > 1 ? "s" : ""} in force`);

  return (
    <header className="dk-pagebar">
      <div>
        <h1>Desk</h1>
        <p>{desk ? parts.join(" · ") : "Loading the desk…"}</p>
      </div>
      <div className="dk-pagebar-actions">
        <button type="button" className="dk-btn is-outline" onClick={onRefresh} disabled={refreshing}>
          <RefreshCw className={refreshing ? "dk-spin" : ""} aria-hidden /> Refresh
        </button>
        <button type="button" className="dk-btn is-lime" onClick={onRun} disabled={running || cyc.running}>
          <Zap aria-hidden /> {running || cyc.running ? "Cycle running…" : "Run a cycle now"}
        </button>
      </div>
    </header>
  );
}

// ── The read ──────────────────────────────────────────────────────────────────

const LANES = [
  { key: "bearish", label: "Bearish" },
  { key: "neutral", label: "Neutral" },
  { key: "bullish", label: "Bullish" },
  { key: "none",    label: "No report" },
];

function TheRead({ desk, onOpen }) {
  const read = desk?.read || {};
  const facts = desk?.facts || {};
  const agents = desk?.agents || [];
  const byLane = Object.fromEntries(LANES.map(l => [l.key, agents.filter(a => a.stance === l.key)]));

  const factBits = [];
  if (facts.regime) factBits.push(`${facts.regime.toLowerCase()} regime${facts.trend ? ` (${facts.trend.toLowerCase()})` : ""}`);
  if (facts.realized_pnl != null) {
    const v = Number(facts.realized_pnl);
    factBits.push(`realized P&L ${v < 0 ? "−" : "+"}$${Math.abs(v).toLocaleString(undefined, { maximumFractionDigits: 2 })} across ${facts.accounts} accounts`);
  }
  if (facts.open_positions != null) factBits.push(`${facts.open_positions === 0 ? "no" : facts.open_positions} open position${facts.open_positions === 1 ? "" : "s"}`);

  return (
    <section className="dk-card dk-read" aria-labelledby="dk-read-title">
      <h2 id="dk-read-title">{read.headline || "Reading the desk…"}</h2>
      {read.sentences?.length ? <p className="dk-read-body">{read.sentences.join(" ")}</p> : null}

      <div className="dk-lanes" role="list" aria-label="Where each agent stands">
        {LANES.map(l => {
          const list = byLane[l.key] || [];
          return (
            <div key={l.key} className={`dk-lane is-${l.key}`} role="listitem" style={{ flexGrow: Math.max(1, list.length) }}>
              <h3>{l.label} <span>{list.length}</span></h3>
              {list.length ? (
                <div className="dk-lane-agents">
                  {list.map(a => (
                    <button key={a.name} type="button" className="dk-lane-agent" onClick={() => onOpen(a.name)}
                      aria-label={`Talk to ${a.name}`}>
                      <Avatar agent={a} size="sm" />
                      <span>{a.name}</span>
                    </button>
                  ))}
                </div>
              ) : <p className="dk-lane-empty">Nobody</p>}
            </div>
          );
        })}
      </div>

      {factBits.length ? (
        <p className="dk-facts"><strong>Everyone is working from:</strong> {factBits.join(" · ")}</p>
      ) : null}
    </section>
  );
}

// ── Needs you ─────────────────────────────────────────────────────────────────

function RequestGroup({ type, reqs, tz, onApply, onDismiss, busy }) {
  const [picked, setPicked] = useState(null);
  const sel = reqs.find(r => r.rec_id === picked) || null;
  const n = reqs.length;
  const who = n === 1 ? reqs[0].specialist : `${numWord(n)} agents`;
  const title = `${who} ${n === 1 ? "wants" : "want"} ${TYPE_ASK[type] || "a decision"}`;
  const dismissLabel = n === 1 ? "Dismiss it" : n === 2 ? "Dismiss both" : `Dismiss all ${numWord(n, true)}`;

  // Keyboard: arrow keys move through the radio rows.
  const onKey = (e, i) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    e.preventDefault();
    const next = reqs[(i + (e.key === "ArrowDown" ? 1 : n - 1)) % n];
    setPicked(next.rec_id);
    document.getElementById(`dk-req-${next.rec_id}`)?.focus();
  };

  return (
    <div className="dk-req-group">
      <h3>{title}</h3>
      <div className="dk-req-rows" role="radiogroup" aria-label={title}>
        {reqs.map((r, i) => {
          const on = r.rec_id === picked;
          return (
            <button
              key={r.rec_id}
              id={`dk-req-${r.rec_id}`}
              type="button"
              role="radio"
              aria-checked={on}
              tabIndex={on || (!picked && i === 0) ? 0 : -1}
              className={`dk-req-row ${on ? "is-on" : ""}`}
              onClick={() => setPicked(on ? null : r.rec_id)}
              onKeyDown={(e) => onKey(e, i)}
            >
              <span className="dk-req-top">
                <span className="dk-radio" aria-hidden />
                <code>{r.offer}</code>
                <span className="dk-req-who">{r.specialist}{r.conviction != null ? ` · ${r.conviction}%` : ""}</span>
                <span className="dk-req-age">{fmtAge(r.created_at)}</span>
              </span>
              <span className={`dk-req-reason ${on ? "is-full" : ""}`}>{r.reason}</span>
              {on ? (
                <span className="dk-req-meta">
                  Raised {fmtAge(r.created_at)}
                  {r.restated_at && r.restated_at !== r.created_at ? `, restated ${fmtAge(r.restated_at)}` : ""}
                  {r.expires_at ? ` · request lapses ${fmtTime(r.expires_at, tz)}` : ""}
                  {r.scope_note ? <><br />{r.scope_note}</> : null}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
      <div className="dk-req-actions">
        <button type="button" className="dk-btn is-lime" disabled={!sel || busy} onClick={() => onApply(sel, reqs)}>
          {busy === "apply" ? "Applying…" : sel ? sel.apply_label : "Pick one to apply it"}
        </button>
        <button type="button" className="dk-btn is-outline" disabled={!!busy} onClick={() => onDismiss(reqs)}>
          {busy === "dismiss" ? "Dismissing…" : dismissLabel}
        </button>
      </div>
      <p className="dk-reassure">{TYPE_REASSURE[type] || TYPE_REASSURE.raise_confidence}</p>
    </div>
  );
}

function NeedsYou({ desk, outcome, setOutcome, reload, needsRef }) {
  const tz = desk?.timezone;
  const reqs = useMemo(() => desk?.requests || [], [desk]);
  const active = desk?.active || [];
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  const groups = useMemo(() => {
    const m = new Map();
    for (const r of reqs) {
      if (!m.has(r.type)) m.set(r.type, []);
      m.get(r.type).push(r);
    }
    return [...m.entries()].sort((a, b) => b[1].length - a[1].length);
  }, [reqs]);

  const apply = async (rec, group) => {
    setBusy("apply"); setError(null);
    try {
      const res = await postJSON(`/team/apply/${rec.rec_id}`);
      if (res.error || res.success === false) throw new Error(res.error || "Could not apply");
      setOutcome({
        kind: "applied", effect: rec.effect, from: rec.specialist, until: res.active_until,
        ids: [rec.rec_id, ...(res.covered || [])], covered: (res.covered || []).length,
        groupSize: group.length,
      });
      await reload();
    } catch (e) { setError(e.message); }
    setBusy(null);
  };

  const dismiss = async (group) => {
    setBusy("dismiss"); setError(null);
    try {
      const ids = group.map(r => r.rec_id);
      const res = await postJSON("/team/dismiss", { rec_ids: ids });
      if (res.error) throw new Error(res.error);
      setOutcome({ kind: "dismissed", ids, count: ids.length });
      await reload();
    } catch (e) { setError(e.message); }
    setBusy(null);
  };

  const undo = async (ids) => {
    setBusy("undo"); setError(null);
    try {
      const res = await postJSON("/team/undo", { rec_ids: ids });
      if (res.error) throw new Error(res.error);
      setOutcome(null);
      await reload();
    } catch (e) { setError(e.message); }
    setBusy(null);
  };

  let body;
  if (outcome?.kind === "applied") {
    body = (
      <div className="dk-confirm" role="status">
        <h3>{outcome.effect}</h3>
        <p>
          From {outcome.from} · ends {fmtTime(outcome.until, tz)}.
          {outcome.covered ? ` The other ${numWord(outcome.covered, true)} request${outcome.covered > 1 ? "s" : ""} closed as covered.` : ""}
        </p>
        <div className="dk-req-actions">
          <button type="button" className="dk-btn is-outline" onClick={() => undo(outcome.ids)} disabled={!!busy}>
            {busy === "undo" ? "Undoing…" : "Undo"}
          </button>
          <button type="button" className="dk-btn is-quiet" onClick={() => setOutcome(null)}>Done</button>
        </div>
      </div>
    );
  } else if (outcome?.kind === "dismissed") {
    body = (
      <div className="dk-confirm" role="status">
        <h3>Dismissed {outcome.count === 1 ? "the request" : `${numWord(outcome.count, true)} requests`}</h3>
        <p>Nothing changes in trading.</p>
        <div className="dk-req-actions">
          <button type="button" className="dk-btn is-outline" onClick={() => undo(outcome.ids)} disabled={!!busy}>
            {busy === "undo" ? "Undoing…" : "Undo"}
          </button>
          <button type="button" className="dk-btn is-quiet" onClick={() => setOutcome(null)}>Done</button>
        </div>
      </div>
    );
  } else if (groups.length) {
    body = groups.map(([type, list]) => (
      <RequestGroup key={type + list.map(r => r.rec_id).join()} type={type} reqs={list} tz={tz}
        onApply={apply} onDismiss={dismiss} busy={busy} />
    ));
  }

  const quiet = !body;
  return (
    <section ref={needsRef} id="dk-needs" tabIndex={-1}
      className={`dk-card dk-needs ${quiet ? "is-quiet" : ""}`} aria-labelledby="dk-needs-title">
      <h2 id="dk-needs-title" className="dk-sr">Needs you</h2>
      {quiet ? <p className="dk-quiet-line">Nothing needs you right now.</p> : body}
      {error ? <p className="dk-error" role="alert">{error}</p> : null}
      {active.length && !outcome ? (
        <div className="dk-inforce">
          {active.map(g => (
            <div key={g.rec_id}>
              <p><strong>In force:</strong> {g.effect} · from {g.specialist} · ends {fmtTime(g.active_until, tz)}</p>
              <button type="button" className="dk-btn is-quiet is-small" onClick={() => undo([g.rec_id])} disabled={!!busy}>
                Lift it
              </button>
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
}

// ── Team grid ─────────────────────────────────────────────────────────────────

function AgentCard({ agent, inQuestion, onTalk, onToggleQuestion, onJumpToRequest, talking }) {
  const quiet = agent.stance === "none" && !agent.always_on;
  return (
    <article className={`dk-card dk-agent is-${agent.stance} ${talking ? "is-talking" : ""}`}
      aria-labelledby={`dk-agent-${agent.name}`}>
      <header className="dk-agent-head">
        <Avatar agent={agent} />
        <div>
          <h3 id={`dk-agent-${agent.name}`}>{agent.name}</h3>
          <p>{agent.title}</p>
        </div>
        <StancePill agent={agent} />
      </header>
      {agent.request ? (
        <button type="button" className="dk-link dk-agent-want" onClick={onJumpToRequest}>{wantsLabel(agent.request)}</button>
      ) : null}
      <p className={`dk-take ${quiet || !agent.headline ? "is-muted" : ""}`}>
        {agent.headline || (agent.always_on ? "Watching quietly — no reflection yet." : "No report this cycle.")}
      </p>
      {agent.stats?.length ? (
        <ul className="dk-chips" aria-label={`${agent.name}'s key numbers`}>
          {agent.stats.map(s => (
            <li key={s.label}><span>{s.label}</span><code>{s.value}</code></li>
          ))}
        </ul>
      ) : null}
      <div className="dk-agent-actions">
        <button type="button" className="dk-btn is-cream" onClick={onTalk} aria-pressed={talking}>Talk</button>
        <button type="button" className={`dk-btn ${inQuestion ? "is-selected" : "is-outline"}`}
          aria-pressed={inQuestion} onClick={onToggleQuestion}>
          {inQuestion ? "In the question" : "Add to question"}
        </button>
      </div>
    </article>
  );
}

const FILTERS = [
  { key: "all",     label: "Everyone",          test: () => true },
  { key: "leaning", label: "Leaning",           test: isLeaning },
  { key: "neutral", label: "Neutral",           test: a => a.stance === "neutral" },
  { key: "request", label: "Wants a decision",  test: a => !!a.request },
];

// ── Conversations (shared by the pane and the round view) ─────────────────────

let _msgSeq = 0;
const msgId = () => `m${Date.now()}_${_msgSeq++}`;

function useThreads() {
  const [threads, setThreads] = useState({});
  const ref = useRef(threads);
  ref.current = threads;

  const patch = useCallback((name, fn) => {
    setThreads(prev => {
      const cur = prev[name] || { messages: [], threadId: null, loaded: false, pending: 0 };
      return { ...prev, [name]: fn(cur) };
    });
  }, []);

  const load = useCallback(async (name) => {
    const cur = ref.current[name];
    if (cur?.loaded || cur?.loading) return;
    patch(name, t => ({ ...t, loading: true }));
    try {
      const d = await getJSON(`/team/chat/${name}`);
      const msgs = (d.messages || []).map(m => ({ ...m, id: msgId() }));
      const seen = new Set(msgs.map(m => `${m.role}|${m.content}`));
      patch(name, t => ({
        ...t, loading: false, loaded: true,
        // History first, then anything sent this session that history doesn't
        // already hold (a round question can land before the history does).
        messages: [...msgs, ...t.messages.filter(m => !seen.has(`${m.role}|${m.content}`))],
        threadId: t.threadId || (msgs.length ? msgs[msgs.length - 1].thread_id : null),
      }));
    } catch {
      patch(name, t => ({ ...t, loading: false, loaded: true }));
    }
  }, [patch]);

  // Send one question; the reply is inserted directly after ITS question, so
  // replies can never render above what prompted them.
  const send = useCallback(async (name, text) => {
    const qid = msgId();
    patch(name, t => ({
      ...t, pending: t.pending + 1,
      messages: [...t.messages, { id: qid, role: "user", content: text, ts: new Date().toISOString() }],
    }));
    let reply, ok = true;
    try {
      const d = await postJSON("/team/chat", {
        specialist: name, message: text, thread_id: ref.current[name]?.threadId || null, context: "desk",
      });
      if (d.error) { ok = false; reply = d.error; }
      else {
        reply = d.reply;
        patch(name, t => ({ ...t, threadId: d.thread_id || t.threadId }));
      }
    } catch {
      ok = false; reply = "Network error. Check your connection and try again.";
    }
    patch(name, t => {
      const i = t.messages.findIndex(m => m.id === qid);
      const answer = { id: msgId(), role: "assistant", content: reply, ts: new Date().toISOString(), failed: !ok };
      const messages = [...t.messages];
      // after the question and any earlier answers to it
      let at = i + 1;
      while (at < messages.length && messages[at].replyTo === qid) at++;
      messages.splice(i < 0 ? messages.length : at, 0, { ...answer, replyTo: qid });
      return { ...t, pending: Math.max(0, t.pending - 1), messages };
    });
    return { ok, reply };
  }, [patch]);

  return { threads, load, send };
}

function ConversationPane({ agent, desk, thread, onLoad, onSend, onClose, onJumpToRequest, focusKey }) {
  const [input, setInput] = useState("");
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const tz = desk?.timezone;

  useEffect(() => { onLoad(agent.name); }, [agent.name, onLoad]);
  useEffect(() => {
    // On touch screens focusing would throw up the keyboard over the brief.
    if (window.matchMedia("(pointer: fine)").matches) inputRef.current?.focus({ preventScroll: true });
  }, [agent.name, focusKey]);
  // Scroll the pane itself — scrollIntoView would drag the whole page along.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [thread?.messages?.length, thread?.pending]);

  const submit = (text) => {
    const t = (text ?? input).trim();
    if (!t) return;
    setInput("");
    onSend(agent.name, t);
  };

  const msgs = thread?.messages || [];
  return (
    <aside className="dk-pane" aria-label={`Conversation with ${agent.name}`}>
      <header className="dk-pane-head">
        <Avatar agent={agent} />
        <div>
          <h2>{agent.name}</h2>
          <p>{agent.title}</p>
        </div>
        <StancePill agent={agent} />
        <button type="button" className="dk-icon-btn" onClick={onClose} aria-label={`Close conversation with ${agent.name}`}>
          <X aria-hidden />
        </button>
      </header>

      <div className="dk-pane-scroll" ref={scrollRef}>
        {agent.brief ? (
          <section className="dk-brief">
            <h3>Latest brief <span>{agent.published_at ? fmtTime(agent.published_at, tz) : ""}</span></h3>
            <p>{agent.brief}</p>
            {agent.request ? (
              <button type="button" className="dk-link" onClick={onJumpToRequest}>
                {wantsLabel(agent.request)} — see Needs you
              </button>
            ) : null}
          </section>
        ) : (
          <p className="dk-muted">{agent.name} has not published a brief yet.</p>
        )}

        <ol className="dk-thread" aria-live="polite">
          {thread?.loading ? <li className="dk-muted">Loading the conversation…</li> : null}
          {msgs.map(m => (
            <li key={m.id} className={`dk-msg ${m.role === "user" ? "is-you" : "is-agent"} ${m.failed ? "is-failed" : ""}`}>
              <span className="dk-msg-who">{m.role === "user" ? "You" : agent.name} · {fmtTime(m.ts, tz)}</span>
              <p>{m.content}</p>
            </li>
          ))}
          {thread?.pending ? (
            <li className="dk-msg is-agent is-typing"><span className="dk-msg-who">{agent.name} is thinking…</span></li>
          ) : null}
        </ol>
      </div>

      <div className="dk-starters">
        {STARTERS.map(s => (
          <button key={s} type="button" className="dk-chip-btn" onClick={() => submit(s)}>{s}</button>
        ))}
      </div>
      <form className="dk-composer" onSubmit={(e) => { e.preventDefault(); submit(); }}>
        <label htmlFor="dk-pane-input" className="dk-sr">Message {agent.name}</label>
        <textarea id="dk-pane-input" ref={inputRef} rows={2} value={input} enterKeyHint="send"
          placeholder={`Message ${agent.name}…`}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); } }} />
        <button type="submit" className="dk-btn is-lime" disabled={!input.trim()}>
          <Send aria-hidden /> Send
        </button>
      </form>
    </aside>
  );
}

// ── Ask bar ───────────────────────────────────────────────────────────────────

function AskBar({ agents, selected, onRemove, onClear, onAsk }) {
  const [text, setText] = useState("");
  const count = selected.length || agents.length;
  const label = selected.length === 1 ? "Send" : `Ask ${selected.length ? count : `all ${count}`}`;
  const submit = (e) => {
    e?.preventDefault();
    const t = text.trim();
    if (!t) return;
    onAsk(t);
    setText("");
  };
  return (
    <form className="dk-askbar" onSubmit={submit} aria-label="Ask the desk">
      <div className="dk-ask-to">
        <span className="dk-ask-label">To</span>
        {selected.length === 0 ? (
          <span className="dk-to-chip is-all">The whole desk</span>
        ) : selected.map(name => (
          <button key={name} type="button" className="dk-to-chip" onClick={() => onRemove(name)}
            aria-label={`Remove ${name} from the question`}>
            {name} <X aria-hidden />
          </button>
        ))}
        {selected.length > 1 ? (
          <button type="button" className="dk-link is-small" onClick={onClear}>Clear</button>
        ) : null}
      </div>
      <div className="dk-ask-row">
        <label htmlFor="dk-ask-input" className="dk-sr">Your question</label>
        <input id="dk-ask-input" type="text" value={text} onChange={e => setText(e.target.value)}
          placeholder={selected.length === 1 ? `Ask ${selected[0]} something…` : "Ask a question…"}
          autoComplete="off" enterKeyHint="send" />
        <button type="submit" className="dk-btn is-cream" disabled={!text.trim()}>
          <Send aria-hidden /> {label}
        </button>
      </div>
      <div className="dk-ask-suggest">
        {SUGGESTED.map(s => (
          <button key={s} type="button" className="dk-chip-btn"
            onMouseDown={e => e.preventDefault()} onClick={() => setText(s)}>{s}</button>
        ))}
      </div>
    </form>
  );
}

// The shell's .arena-root has overflow-x:hidden, which makes it the scroll
// container for `position: sticky` — a sticky bar inside it never pins. So the
// ask bar is fixed to the viewport while the team section is on screen, lined
// up with the team column, and the section reserves room for it.
function useFloatingBar(sectionRef, barRef) {
  const [box, setBox] = useState(null);       // {left, width} or null when hidden
  const [height, setHeight] = useState(0);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return undefined;
    let visible = false;
    let frame = 0;
    const place = () => {
      const r = el.getBoundingClientRect();
      // Trust the geometry, not only the observer's last callback (it can lag
      // behind content that loads in and pushes the section off screen).
      visible = r.top < window.innerHeight && r.bottom > 0;
      if (!visible) { setBox(null); return; }
      setBox(b => (b && b.left === r.left && b.width === r.width ? b : { left: r.left, width: r.width }));
    };
    const io = new IntersectionObserver(([e]) => { visible = e.isIntersecting; place(); });
    io.observe(el);
    const ro = new ResizeObserver(place);
    ro.observe(el);
    const onScroll = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(place); };
    window.addEventListener("resize", place);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      io.disconnect(); ro.disconnect(); cancelAnimationFrame(frame);
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", onScroll);
    };
  }, [sectionRef]);

  useEffect(() => {
    const el = barRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(() => setHeight(el.offsetHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, [barRef, box !== null]);  // eslint-disable-line react-hooks/exhaustive-deps

  return { box, height };
}

// ── Round view ────────────────────────────────────────────────────────────────

function RoundView({ round, agentsByName, onBack, onFollowUp }) {
  const answers = round.names.map(n => ({ agent: agentsByName[n], ...round.answers[n] })).filter(a => a.agent);
  const done = answers.filter(a => a.status !== "waiting").length;
  return (
    <section className="dk-round" aria-labelledby="dk-round-q">
      <button type="button" className="dk-btn is-outline" onClick={onBack}>
        <ArrowLeft aria-hidden /> Back to the team
      </button>
      <div className="dk-round-q">
        <p className="dk-round-meta">You asked {numWord(round.names.length, true)} agents</p>
        <h2 id="dk-round-q">{round.question}</h2>
      </div>
      <p className="dk-round-synth" aria-live="polite">
        {round.synthesis
          ? round.synthesis
          : done < answers.length
            ? `${done} of ${answers.length} have answered. The summary appears once everyone has.`
            : round.synthError ? "Could not summarise the answers this time." : "Summarising where they agree and split…"}
      </p>
      <div className="dk-round-cols">
        {answers.map(({ agent, status, text }) => (
          <article key={agent.name} className={`dk-card dk-round-col is-${agent.stance}`} aria-busy={status === "waiting"}>
            <header className="dk-agent-head">
              <Avatar agent={agent} />
              <div><h3>{agent.name}</h3><p>{agent.title}</p></div>
            </header>
            <StancePill agent={agent} />
            <p className={`dk-round-answer ${status === "waiting" ? "is-muted" : ""} ${status === "failed" ? "is-failed" : ""}`}>
              {status === "waiting" ? `${agent.name} is thinking…` : text}
            </p>
            <button type="button" className="dk-btn is-outline" onClick={() => onFollowUp(agent.name)}
              disabled={status === "waiting"}>
              Follow up with {agent.name}
            </button>
          </article>
        ))}
      </div>
    </section>
  );
}

// ── Council ───────────────────────────────────────────────────────────────────

const VERDICT_WORD = { BULLISH: "bullish", BEARISH: "bearish", NEUTRAL: "neutral", CAUTION: "caution", VOLATILE: "volatile" };
const PHASE_LABEL = { position: "Opening position", reaction: "Reaction", verdict: "Moderator's verdict" };

function DebatePlayback({ council, tz }) {
  const turns = (council?.turns || []).filter(t => t.text);
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);   // never autoplays
  const [transcript, setTranscript] = useState(false);
  const turn = turns[idx];

  useEffect(() => { setIdx(0); setPlaying(false); }, [council?.council_id]);
  useEffect(() => {
    if (!playing || !turn) return undefined;
    const ms = Math.max(4000, Math.min(12000, turn.text.length * 45));
    const id = setTimeout(() => {
      if (idx + 1 < turns.length) setIdx(idx + 1); else setPlaying(false);
    }, ms);
    return () => clearTimeout(id);
  }, [playing, idx, turn, turns.length]);

  if (!turns.length) return <p className="dk-muted">This council has no recorded debate.</p>;
  const speakers = [...new Map(turns.map(t => [t.speaker, t])).values()];

  return (
    <div className="dk-debate">
      <div className="dk-debate-speakers" role="group" aria-label="Jump to a speaker">
        {speakers.map(s => (
          <button key={s.speaker} type="button"
            className={`dk-chip-btn ${turn?.speaker === s.speaker ? "is-on" : ""}`}
            onClick={() => { setIdx(turns.findIndex(t => t.speaker === s.speaker)); setPlaying(false); }}>
            {s.speaker}
          </button>
        ))}
      </div>
      <article className="dk-debate-turn" aria-live="polite">
        <header>
          <span className="dk-avatar" style={{ "--agent": turn.color || "#a3aa9b" }} aria-hidden>{turn.emoji || "•"}</span>
          <div>
            <h4>{turn.speaker}</h4>
            <p>{PHASE_LABEL[turn.phase] || "Turn"}{turn.role ? ` · ${turn.role}` : ""}</p>
          </div>
          {turn.signal ? (
            <span className={`dk-pill is-${turn.signal === "BEARISH" ? "bearish" : turn.signal === "BULLISH" ? "bullish" : "neutral"}`}>
              {VERDICT_WORD[turn.signal] ? VERDICT_WORD[turn.signal][0].toUpperCase() + VERDICT_WORD[turn.signal].slice(1) : turn.signal}
              {turn.confidence != null ? ` ${turn.confidence}%` : ""}
            </span>
          ) : null}
        </header>
        <p>{turn.text}</p>
      </article>
      <div className="dk-debate-controls">
        <button type="button" className="dk-icon-btn" onClick={() => { setIdx(Math.max(0, idx - 1)); setPlaying(false); }}
          disabled={idx === 0} aria-label="Previous turn"><ChevronLeft aria-hidden /></button>
        <button type="button" className="dk-btn is-outline" onClick={() => {
          if (!playing && idx >= turns.length - 1) setIdx(0);
          setPlaying(p => !p);
        }}>
          {playing ? <><Pause aria-hidden /> Pause</> : <><Play aria-hidden /> {idx >= turns.length - 1 ? "Replay" : "Play"}</>}
        </button>
        <button type="button" className="dk-icon-btn" onClick={() => { setIdx(Math.min(turns.length - 1, idx + 1)); setPlaying(false); }}
          disabled={idx >= turns.length - 1} aria-label="Next turn"><ChevronRight aria-hidden /></button>
        <span className="dk-muted">Turn {idx + 1} of {turns.length}</span>
        <button type="button" className="dk-link" onClick={() => setTranscript(v => !v)} aria-expanded={transcript}>
          {transcript ? "Hide the full transcript" : "Read the full transcript"}
        </button>
      </div>
      {transcript ? (
        <ol className="dk-transcript">
          {turns.map((t, i) => (
            <li key={i}>
              <strong>{t.speaker}</strong> <span className="dk-muted">{PHASE_LABEL[t.phase] || ""}</span>
              <p>{t.text}</p>
            </li>
          ))}
        </ol>
      ) : null}
    </div>
  );
}

function CouncilCard({ tz }) {
  const [council, setCouncil] = useState(null);
  const [loading, setLoading] = useState(true);
  const [convening, setConvening] = useState(false);
  const [showSplit, setShowSplit] = useState(false);
  const [showDebate, setShowDebate] = useState(false);
  // Which brain reasons over THIS convened run: "local" = the PC model, "claude"
  // = cloud Claude. Per-run only — the autonomous council keeps its default.
  const [brain, setBrain] = useState("local");

  const load = useCallback(async () => {
    try {
      const d = await getJSON("/team/council/latest");
      setCouncil(d.council || null);
    } catch { /* keep last */ } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [load]);

  const convene = async () => {
    setConvening(true);
    try {
      await getJSON(`/team/council/run?provider=${brain}`, { method: "POST" });
      const prevId = council?.council_id;
      for (let i = 0; i < 12; i++) {
        await new Promise(r => setTimeout(r, 6000));
        const d = await getJSON("/team/council/latest");
        if (d.council && d.council.council_id !== prevId) { setCouncil(d.council); break; }
      }
    } catch { /* surfaced by the unchanged card */ }
    setConvening(false);
  };

  const stance = council?.final_stance;
  const seated = council?.participants?.length || 0;
  const agreements = council?.agreements || [];
  const conflicts = council?.conflicts || [];

  return (
    <section className="dk-card dk-council" aria-labelledby="dk-council-title">
      <div className="dk-council-head">
        <div>
          <h2 id="dk-council-title">Council</h2>
          {council ? (
            <p className="dk-muted">Met {fmtTime(council.created_at, tz, { day: true })} · {seated} sat{council.trigger ? ` · ${council.trigger}` : ""}</p>
          ) : null}
        </div>
        <div className="dk-council-tools">
          <div className="dk-seg" role="radiogroup" aria-label="Model for the next convened council">
            {[{ k: "local", label: "PC model" }, { k: "claude", label: "Claude" }].map(o => (
              <button key={o.k} type="button" role="radio" aria-checked={brain === o.k}
                className={brain === o.k ? "is-on" : ""} onClick={() => setBrain(o.k)} disabled={convening}>
                {o.label}
              </button>
            ))}
          </div>
          <button type="button" className="dk-btn is-outline" onClick={convene} disabled={convening}>
            <Zap className={convening ? "dk-spin" : ""} aria-hidden /> {convening ? "Convening…" : "Convene now"}
          </button>
        </div>
      </div>

      {loading ? <p className="dk-muted">Loading the latest council…</p> : !council ? (
        <p className="dk-muted">No council yet. Convene one, or wait for the next cycle — it meets after each.</p>
      ) : (
        <>
          <h3 className="dk-verdict">
            The council said {VERDICT_WORD[stance] || String(stance || "nothing").toLowerCase()}
            {council.final_confidence != null ? `, at ${council.final_confidence}%` : ""}.
          </h3>
          <p className="dk-council-body">
            {council.topic ? <><strong>The question:</strong> {council.topic} </> : null}
            {council.summary}
            {council.action_result ? ` Verdict action: ${council.action_result.type} — ${council.action_result.status}.` : ""}
          </p>
          <div className="dk-council-actions">
            {(agreements.length || conflicts.length) ? (
              <button type="button" className="dk-btn is-outline" onClick={() => setShowSplit(v => !v)} aria-expanded={showSplit}>
                {showSplit ? "Hide agreements and disputes" : "Where they agree, and where they don't"}
              </button>
            ) : null}
            <button type="button" className="dk-btn is-outline" onClick={() => setShowDebate(v => !v)} aria-expanded={showDebate}>
              {showDebate ? "Close the debate" : "Watch the debate"}
            </button>
          </div>
          {showSplit ? (
            <div className="dk-split">
              <div>
                <h4>Where they agree</h4>
                {agreements.length ? <ul>{agreements.map((a, i) => <li key={i}>{a}</li>)}</ul> : <p className="dk-muted">Nothing recorded.</p>}
              </div>
              <div>
                <h4>Still arguing</h4>
                {conflicts.length ? <ul>{conflicts.map((c, i) => <li key={i}>{c}</li>)}</ul> : <p className="dk-muted">Nothing recorded.</p>}
              </div>
            </div>
          ) : null}
          {showDebate ? <DebatePlayback council={council} tz={tz} /> : null}
        </>
      )}
    </section>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function TeamPage() {
  const [desk, setDesk] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [running, setRunning] = useState(false);
  const [openName, setOpenName] = useState(null);
  const [focusKey, setFocusKey] = useState(0);
  const [selected, setSelected] = useState([]);
  const [filter, setFilter] = useState("all");
  const [round, setRound] = useState(null);
  const [outcome, setOutcome] = useState(null);
  const needsRef = useRef(null);
  const teamRef = useRef(null);
  const askRef = useRef(null);
  const { box: askBox, height: askHeight } = useFloatingBar(teamRef, askRef);
  const [narrow, setNarrow] = useState(() => window.matchMedia("(max-width: 1099px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1099px)");
    const on = () => setNarrow(mq.matches);
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);
  const { threads, load: loadThread, send } = useThreads();

  const load = useCallback(async () => {
    try {
      const d = await getJSON("/team/desk");
      if (d.error && !d.agents?.length) throw new Error(d.error);
      setDesk(d);
      setLoadError(null);
    } catch (e) {
      setLoadError(e.message || "The desk service did not answer.");
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);

  const refresh = async () => { setRefreshing(true); await load(); setRefreshing(false); };

  const runCycle = async () => {
    setRunning(true);
    try {
      await getJSON("/team/run", { method: "POST" });
      // A full cycle takes a minute or two; poll until a newer one lands.
      const before = desk?.cycle?.at;
      for (let i = 0; i < 30; i++) {
        await new Promise(r => setTimeout(r, 8000));
        const d = await getJSON("/team/desk");
        setDesk(d);
        if (d.cycle?.at && d.cycle.at !== before && !d.cycle.running) break;
      }
    } catch { /* the status line shows the last known state */ }
    setRunning(false);
  };

  const agents = useMemo(() => desk?.agents || [], [desk]);
  const agentsByName = useMemo(() => Object.fromEntries(agents.map(a => [a.name, a])), [agents]);
  const sorted = useMemo(() => [...agents].sort((a, b) =>
    (STANCE_ORDER[a.stance] - STANCE_ORDER[b.stance])
    || ((b.conviction ?? -1) - (a.conviction ?? -1))
    || a.name.localeCompare(b.name)), [agents]);
  const activeFilter = FILTERS.find(f => f.key === filter) || FILTERS[0];
  const visible = sorted.filter(activeFilter.test);

  const openAgent = (name) => {
    setOpenName(name);
    setFocusKey(k => k + 1);
    // Bring the pane into view: above the grid on narrow screens, beside it on
    // wide ones (the grid stays usable next to it).
    requestAnimationFrame(() => {
      const pane = document.querySelector(".dk-pane");
      if (!pane) return;
      const top = pane.getBoundingClientRect().top;
      if (top < 0 || top > window.innerHeight * 0.35) {
        window.scrollBy({ top: top - 14, behavior: "smooth" });
      }
    });
  };
  const jumpToNeeds = () => {
    needsRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    needsRef.current?.focus({ preventScroll: true });
  };
  const toggleSelected = (name) =>
    setSelected(s => (s.includes(name) ? s.filter(n => n !== name) : [...s, name]));

  const ask = async (question) => {
    const names = selected.length ? selected : agents.map(a => a.name);
    if (names.length === 1) {
      openAgent(names[0]);
      send(names[0], question);
      return;
    }
    const id = Date.now();
    setRound({ id, question, names, answers: Object.fromEntries(names.map(n => [n, { status: "waiting" }])), synthesis: null });
    teamRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

    // Each answer lands on its own; three in flight at a time keeps the local
    // model from being flooded by eleven requests at once.
    const queue = [...names];
    const results = {};
    const worker = async () => {
      while (queue.length) {
        const name = queue.shift();
        const { ok, reply } = await send(name, question);
        results[name] = { ok, reply };
        setRound(r => (r && r.id === id
          ? { ...r, answers: { ...r.answers, [name]: { status: ok ? "done" : "failed", text: reply } } }
          : r));
      }
    };
    await Promise.all([worker(), worker(), worker()]);

    const good = names.filter(n => results[n]?.ok).map(n => ({
      name: n, stance: agentsByName[n]?.stance, conviction: agentsByName[n]?.conviction, answer: results[n].reply,
    }));
    if (good.length < 2) { setRound(r => (r && r.id === id ? { ...r, synthError: true } : r)); return; }
    try {
      const d = await postJSON("/team/round/synthesis", { question, answers: good });
      setRound(r => (r && r.id === id ? { ...r, synthesis: d.synthesis, synthError: !d.synthesis } : r));
    } catch {
      setRound(r => (r && r.id === id ? { ...r, synthError: true } : r));
    }
  };

  const openAgentObj = openName ? agentsByName[openName] : null;
  const showAsk = !!(agents.length && askBox && !(narrow && openAgentObj));
  const counts = Object.fromEntries(FILTERS.map(f => [f.key, agents.filter(f.test).length]));

  return (
    <div className="dk">
      <PageBar desk={desk} onRefresh={refresh} onRun={runCycle} running={running} refreshing={refreshing} />

      {loadError && !desk ? (
        <section className="dk-card dk-empty" role="alert">
          <h2>The desk did not answer</h2>
          <p>{loadError}</p>
          <button type="button" className="dk-btn is-outline" onClick={refresh}><RefreshCw aria-hidden /> Try again</button>
        </section>
      ) : null}

      <div className="dk-top">
        <TheRead desk={desk} onOpen={openAgent} />
        <NeedsYou desk={desk} outcome={outcome} setOutcome={setOutcome} reload={load} needsRef={needsRef} />
      </div>

      <section className="dk-team" ref={teamRef} aria-labelledby="dk-team-title"
        style={{ paddingBottom: showAsk ? askHeight + 12 : 0 }}>
        <div className="dk-team-head">
          <h2 id="dk-team-title">The team</h2>
          {!round ? (
            <div className="dk-filters" role="group" aria-label="Filter the team">
              {FILTERS.map(f => (
                <button key={f.key} type="button" aria-pressed={filter === f.key}
                  className={`dk-filter ${filter === f.key ? "is-on" : ""}`} onClick={() => setFilter(f.key)}>
                  {f.label} <span>{counts[f.key]}</span>
                </button>
              ))}
            </div>
          ) : null}
        </div>

        <div className={`dk-team-body ${openAgentObj ? "has-pane" : ""}`}>
          {openAgentObj ? (
            <ConversationPane
              key={openAgentObj.name}
              agent={openAgentObj} desk={desk}
              thread={threads[openAgentObj.name]}
              onLoad={loadThread} onSend={send}
              onClose={() => setOpenName(null)}
              onJumpToRequest={jumpToNeeds}
              focusKey={focusKey}
            />
          ) : null}

          <div className="dk-team-main">
            {round ? (
              <RoundView round={round} agentsByName={agentsByName}
                onBack={() => setRound(null)} onFollowUp={openAgent} />
            ) : visible.length ? (
              <div className="dk-grid">
                {visible.map(a => (
                  <AgentCard key={a.name} agent={a}
                    talking={openName === a.name}
                    inQuestion={selected.includes(a.name)}
                    onTalk={() => openAgent(a.name)}
                    onToggleQuestion={() => toggleSelected(a.name)}
                    onJumpToRequest={jumpToNeeds} />
                ))}
              </div>
            ) : desk ? (
              <p className="dk-muted dk-grid-empty">Nobody matches this filter.</p>
            ) : (
              <p className="dk-muted dk-grid-empty">Loading the team…</p>
            )}
          </div>
        </div>

        {showAsk ? createPortal(
          <div className="dk-float" ref={askRef} style={{ left: askBox.left, width: askBox.width }}>
            <AskBar agents={agents} selected={selected}
              onRemove={toggleSelected} onClear={() => setSelected([])} onAsk={ask} />
          </div>,
          document.body,
        ) : null}
      </section>

      <CouncilCard tz={desk?.timezone} />
    </div>
  );
}
